/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, Suspense, Fragment } from 'react';
import { Await, useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
import { shouldRevalidate as shouldRevalidateProduct } from '@/lib/revalidation/routes/product';
import type { Route } from './+types/_app.product.$productId';
import { type ShopperProducts } from '@/scapi';
import { fetchProductById } from '@/lib/api/products.server';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import ProductView from '@/components/product-view';
import ChildProducts from '@/components/product-view/child-products';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import { CategoryHrefProvider, type CategoryUrlMap } from '@/components/navigation-menu/category-href';
import { collectProductBreadcrumbCategoryUrls } from '@/lib/product/seo-url';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import ProductRecommendations from '@/components/product-recommendations';
import { EINSTEIN_RECOMMENDERS } from '@/lib/product/einstein-recommenders';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { Region } from '@/components/region';
import { ProductProvider } from '@/providers/product-context';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { generateProductSchema } from '@/utils/product-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getLogger } from '@/lib/logger.server';
import { cn, toNumber } from '@/lib/utils';
import { UITarget } from '@/targets/ui-target';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { fetchContentAssetHtml } from '@/lib/api/content-asset.server';
// RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
import { getFibblConfig, type FibblConfig } from '@/lib/fibbl.server';
// END RGBARRY-CUSTOMIZATION
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { selectedStoreContext } from '@/extensions/store-locator/middlewares/selected-store.server';
// RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP — import disabled with its usage below
// import PickupProvider from '@/extensions/bopis/context/pickup-context';
// @sfdc-extension-block-end SFDC_EXT_BOPIS
// @sfdc-extension-block-start SFDC_EXT_BNPL
import {
    getBuyNowPayLaterMessage,
    getBuyNowPayLaterLearnMore,
    type BuyNowPayLaterMessageData,
    type BuyNowPayLaterLearnMoreData,
} from '@/extensions/bnpl/lib/api/bnpl.server';
import { BnplProvider } from '@/extensions/bnpl/context/bnpl-context';
// @sfdc-extension-block-end SFDC_EXT_BNPL
// @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
import {
    getReviewsSummary,
    getReviews,
    getWriteReviewForm,
    type ReviewsSummaryData,
    type ReviewsData,
    type WriteReviewFormData,
} from '@/extensions/ratings-reviews/lib/api/reviews.server';
import { ProductReviewsProvider } from '@/extensions/ratings-reviews/providers/product-reviews-context';
import { WriteReviewFormProvider } from '@/extensions/ratings-reviews/context/write-review-form-context';
// @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
// @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
import {
    getReturnsAndWarranty,
    pdpSectionApi,
    type ReturnsAndWarrantyData,
    type HtmlContent,
} from '@/extensions/product-content/lib/api/product-content.server';
import { resolvePdpSections } from '@/extensions/product-content/lib/pdp-sections';
import { ProductContentDataProvider } from '@/extensions/product-content/context/product-content-data-context';
// @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
// @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
import {
    getEstimatedDelivery,
    type EstimatedDeliveryData,
} from '@/extensions/shipping-delivery/lib/api/shipping-delivery.server';
import { ShippingDeliveryProvider } from '@/extensions/shipping-delivery/context/shipping-delivery-context';
// @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY

@PageType({
    name: 'Product Detail Page',
    description: 'Product detail page with product information, images, and recommendations',
    supportedAspectTypes: ['pdp'],
})
@RegionDefinition([
    {
        id: 'promoContent',
        name: 'Promo Content Region',
        description: 'Promotional content region above main product content',
        maxComponents: 1,
    },
    {
        id: 'engagementContent',
        name: 'Engagement Content Region',
        description: 'Engagement content region for recommendations and related products below main content',
        maxComponents: 1,
    },
])
export class ProductPageMetadata {}

export type ProductPageData = {
    product: ShopperProducts.schemas['Product'];
    // RGBARRY-CUSTOMIZATION (W-23875749): categoryId -> production SEO slug path for the breadcrumb
    // chain. Read SYNCHRONOUSLY off the enriched product response — the product hook stamps `c_seoUrl`
    // onto each node of the embedded `primaryCategory` chain the loader already expands — so breadcrumb
    // links carry prod slugs from the SINGLE product GET with NO extra category fetch (Kevin He review,
    // PR #107). Empty when there's no primary category or the product wasn't enriched (each crumb then
    // falls back to /category/:id).
    categoryUrls: CategoryUrlMap;
    page: ReturnType<typeof fetchPageWithComponentData>;
    pageKey: string;
    pageUrl: string;
    productSchema: Promise<ReturnType<typeof generateProductSchema> | null>;
    // @sfdc-extension-block-start SFDC_EXT_BNPL
    bnplMessage: Promise<BuyNowPayLaterMessageData>;
    bnplLearnMore: Promise<BuyNowPayLaterLearnMoreData>;
    // @sfdc-extension-block-end SFDC_EXT_BNPL
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary: ReviewsSummaryData;
    reviewsList: Promise<ReviewsData>;
    writeReviewForm: Promise<WriteReviewFormData>;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarranty: Promise<ReturnsAndWarrantyData>;
    pdpCollapsibles: Promise<Array<HtmlContent | null>>;
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
    estimatedDelivery: Promise<EstimatedDeliveryData>;
    // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY
    aPlusContent: Promise<string | null>;
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig: FibblConfig;
    // END RGBARRY-CUSTOMIZATION
};

/**
 * Server-side loader function that fetches product data.
 * This function runs on the server during SSR and can access cookies for store information.
 *
 * The product is awaited as critical data: a 404 from SCAPI is re-thrown as
 * `Response(message, { status: 404 })` so React Router renders the 404 page with
 * the proper HTTP status (essential for SEO).
 *
 * @returns Object containing the resolved product, page data, and schema promises
 */
export async function loader(args: Route.LoaderArgs): Promise<ProductPageData> {
    const { request, params, context } = args;
    const logger = getLogger(context);
    const { productId } = params;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const variantPid = searchParams.get('pid');
    logger.debug('Product: loader starting', { productId, variantPid: variantPid || undefined });

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = context.get(selectedStoreContext);
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Get currency from context for product pricing
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Product: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;

    // Resolve the product critically. A 404 here must propagate as Response(404)
    // so the route error boundary renders the 404 page with the correct HTTP status.
    const productLookupId = variantPid || productId;

    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    // Start reviews summary fetch in parallel with the product fetch — it only
    // needs the product ID and drives above-the-fold star display + SEO.
    const reviewsSummaryPromise = getReviewsSummary(productLookupId);
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS

    let product: ShopperProducts.schemas['Product'] | null;
    try {
        product = await fetchProductById(context, productLookupId, {
            expand: [
                'availability', // <-- TTL = 60s (!)
                'bundled_products',
                'images',
                'options',
                'page_meta_tags',
                'prices', // <-- TTL = 900s
                'primary_category',
                'promotions', // <-- TTL = 900s
                'set_products',
                'variations',
            ],
            allImages: true,
            perPricebook: true,
            ...(currency ? { currency } : {}),
            // @sfdc-extension-block-start SFDC_EXT_BOPIS
            // Include inventoryIds parameter when store is selected
            ...(selectedStoreInfo?.inventoryId ? { inventoryIds: [selectedStoreInfo.inventoryId] } : {}),
            // @sfdc-extension-block-end SFDC_EXT_BOPIS
        });
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    if (!product) {
        throw new Response('Product not found', { status: 404 });
    }

    // RGBARRY-CUSTOMIZATION: master product resolved lazily, only for the A+ content asset
    // fallback below (variants inherit the master's c_aPlusContentAssetID). Invoked on demand so a
    // variant that already carries its own asset ID never triggers the extra getProduct request.
    const getMasterProductPromise = () => {
        if (product.master?.masterId) {
            return fetchProductById(context, product.master.masterId, {
                ...(currency ? { currency } : {}),
            });
        }

        return null;
    };

    // Derive the origin from the PUBLIC forwarding headers, NOT `requestUrl.origin` — on Managed
    // Runtime `request.url` carries the internal Lambda/CDN origin, which would leak into the
    // `og:url` this feeds. Mirrors the product-schema URL below (and the content route's og:url).
    const pageUrl = buildCanonicalUrl(getPublicOrigin(request), requestUrl.pathname, requestUrl.search);

    // Generate product schema in loader (server-side) for SEO.
    // Wrapped in a Promise so it can be rendered through Suspense without blocking
    // the loader response. The inner try/catch logs synchronous schema-generation failures
    // (this is local computation, not a SCAPI call — `fetchProductById` already logs SCAPI
    // errors at the API layer) so we keep visibility on rare malformed-input bugs.
    // Render-time failures degrade silently via the route-level <Await errorElement={null}>.
    const productSchemaPromise: Promise<ReturnType<typeof generateProductSchema> | null> = Promise.resolve().then(
        () => {
            try {
                // Use public origin from request headers instead of request.url
                // to avoid exposing internal AWS Lambda URLs in schema
                const publicOrigin = getPublicOrigin(request);
                const url = new URL(request.url);
                const productUrl = `${publicOrigin}${url.pathname}${url.search}`;
                return generateProductSchema(product, productUrl);
            } catch (error) {
                logger.error('Error generating product schema in loader', { error });
                return null;
            }
        }
    );

    // Category context for Page Designer PDP content, sourced from the primary_category
    // expansion (falls back to the flat primaryCategoryId when the expansion is absent).
    const primaryCategoryId = product.primaryCategory?.id ?? product.primaryCategoryId;
    const page = fetchPageWithComponentData(args, {
        aspectType: 'pdp',
        productId: productLookupId,
        ...(primaryCategoryId ? { categoryId: primaryCategoryId } : {}),
    });

    // RGBARRY-CUSTOMIZATION (W-23875749): breadcrumb chain's production SEO slug paths, read straight
    // off the ENRICHED PRODUCT RESPONSE — no extra fetch (Kevin He review, PR #107). The product hook
    // stamps `c_seoUrl` onto each node of the embedded `primaryCategory` chain (leaf + parentCategoryTree)
    // the loader already expanded above, so the slugs ride the SAME single product GET (OOTB-aligned with
    // the mega-menu's `c_seoUrl` convention; verified serializing on bjgk-006 under the primary_category
    // expand). Any crumb without a slug stays unmapped and CategoryBreadcrumbs' useCategoryHref falls
    // back to /category/:id (graceful, never broken).
    const categoryUrls: CategoryUrlMap = collectProductBreadcrumbCategoryUrls(product);

    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    // Await the summary started earlier (ran in parallel with fetchProductById).
    const reviewsSummaryBase = await reviewsSummaryPromise;
    // Prefer SFCC Bazaarvoice custom attributes when present on the product,
    // falling back to the mock summary otherwise.
    const bvRatingRaw = (product as unknown as Record<string, unknown>).c_bvAverageRating;
    const bvCountRaw = (product as unknown as Record<string, unknown>).c_bvReviewCount;
    // toNumber rejects empty/null before coercing: Number('') and Number(null) are
    // both 0 and pass Number.isFinite, so a present-but-empty c_bvReviewCount (common
    // in SFCC) would show "Based on 0 reviews" instead of falling back to the mock.
    const bvRating = toNumber(bvRatingRaw);
    const bvCount = toNumber(bvCountRaw);
    const reviewsSummary =
        Number.isFinite(bvRating) && Number.isFinite(bvCount)
            ? {
                  ...reviewsSummaryBase,
                  averageRating: Math.round(bvRating * 10) / 10,
                  totalCount: bvCount,
                  basedOnLabel: bvCount === 1 ? 'Based on 1 review' : `Based on ${bvCount} reviews`,
              }
            : reviewsSummaryBase;
    const reviewsList = getReviews(productLookupId);
    const writeReviewForm = getWriteReviewForm(productLookupId);
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS

    return {
        product,
        // RGBARRY-CUSTOMIZATION (W-23875749): synchronous — the breadcrumb slug paths are read straight
        // off the product's hook-enriched embedded `primaryCategory` chain (no extra fetch), so links
        // carry prod SEO slugs in the SSR HTML (SEO) and degrade to {} (id-route fallback) on any miss.
        categoryUrls,
        page,
        pageKey: productId,
        pageUrl,
        productSchema: productSchemaPromise,
        // @sfdc-extension-block-start SFDC_EXT_BNPL
        bnplMessage: getBuyNowPayLaterMessage(productLookupId),
        bnplLearnMore: getBuyNowPayLaterLearnMore(productLookupId),
        // @sfdc-extension-block-end SFDC_EXT_BNPL
        // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
        reviewsSummary,
        reviewsList,
        writeReviewForm,
        // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
        // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
        returnsWarranty: getReturnsAndWarranty(productLookupId),
        pdpCollapsibles: Promise.all(
            resolvePdpSections(product).map((section) =>
                pdpSectionApi[section.apiMethod](productLookupId).catch(() => null)
            )
        ),
        // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
        // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
        estimatedDelivery: getEstimatedDelivery(productLookupId),
        // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY
        aPlusContent: (async () => {
            const prod = product as unknown as Record<string, unknown>;
            const ownAssetId =
                typeof prod.c_aPlusContentAssetID === 'string' && prod.c_aPlusContentAssetID.trim()
                    ? prod.c_aPlusContentAssetID
                    : '';
            // Fall back to the master's asset ID only when the variant lacks its own
            const assetId =
                ownAssetId ||
                ((((await getMasterProductPromise()) as unknown as Record<string, unknown> | null)
                    ?.c_aPlusContentAssetID as string | undefined) ??
                    '');
            // fetchContentAssetHtml now returns { html, title, description } | null;
            // the A+ block only renders the body, so unwrap to the HTML string.
            const asset = await fetchContentAssetHtml(context, assetId);
            return asset?.html ?? null;
        })(),
        // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config from site preferences
        fibblConfig: await getFibblConfig(context),
        // END RGBARRY-CUSTOMIZATION
    };
}

// Gates both the navigation axis (skip client-only param changes like color/size) and the action axis (skip the
// expensive loader re-run after cart/wishlist/account mutations that touch nothing the loader reads). The policy and
// its denylist live in the shared module so the rationale is documented and unit-tested in one place.
// RGBARRY-CUSTOMIZATION: `analytics.routeType` classifies this route for the GTM data layer's
// page-view event (see src/analytics/route-analytics.ts). Route-based, so it survives the legacy
// `/<slug>/<id>.html` PDP URL shape that no path-prefix blocklist could match. This handle is
// re-exported by the `.html` PDP route, so that URL is classified identically.
export const handle = {
    ui: { main: { backgroundClass: 'bg-background' } },
    analytics: { routeType: 'product' },
};

export const shouldRevalidate = shouldRevalidateProduct;

// RGBARRY-CUSTOMIZATION: counts <li> tags in the shortDescription HTML so the description accordion
// can switch to a two-column layout for long bullet lists; see usage below.
function countListItems(html: string): number {
    return (html.match(/<li[\s>]/gi) ?? []).length;
}

function ProductContent({
    product,
    url,
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary,
    reviewsList,
    writeReviewForm,
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarrantyPromise,
    pdpCollapsiblesPromise,
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    aPlusContentPromise,
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig,
    // END RGBARRY-CUSTOMIZATION
}: {
    product: ShopperProducts.schemas['Product'];
    url: string;
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary: ReviewsSummaryData;
    reviewsList: Promise<ReviewsData>;
    writeReviewForm: Promise<WriteReviewFormData>;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarrantyPromise: Promise<ReturnsAndWarrantyData>;
    pdpCollapsiblesPromise: Promise<Array<HtmlContent | null>>;
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    aPlusContentPromise: Promise<string | null>;
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig: FibblConfig;
    // END RGBARRY-CUSTOMIZATION
}) {
    const analytics = useAnalytics();
    const lastTrackedProductIdRef = useRef<string | null>(null);

    const primaryImage =
        product.imageGroups?.find((g) => g.viewType === 'large')?.images?.[0]?.link ??
        product.imageGroups?.[0]?.images?.[0]?.link;

    // Track product view on mount and whenever productData changes
    useEffect(() => {
        // Only track if we haven't already tracked this product
        if (product.id !== lastTrackedProductIdRef.current) {
            void analytics.trackViewProduct({
                product,
            });
            lastTrackedProductIdRef.current = product.id;
        }
    }, [analytics, product]);

    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    return (
        <ProductProvider product={product}>
            {/* @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT */}
            <ProductContentDataProvider
                product={product}
                returnsWarrantyPromise={returnsWarrantyPromise}
                pdpCollapsiblesPromise={pdpCollapsiblesPromise}>
                {/* @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT */}
                {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                {/* Provider wraps ProductView so the in-page rating summary shares state
                        with the customer reviews accordion (expand/jump-to coordination). */}
                <ProductReviewsProvider summary={reviewsSummary} reviewsListPromise={reviewsList}>
                    <WriteReviewFormProvider writeReviewFormPromise={writeReviewForm}>
                        {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                        <SeoMeta
                            title={product.name}
                            description={product.pageDescription || product.shortDescription}
                            openGraph={{
                                type: 'product',
                                url,
                                image: primaryImage,
                            }}
                        />
                        <div className="space-y-8">
                            {isProductASet || isProductABundle ? (
                                <>
                                    <ProductView product={product} fibblConfig={fibblConfig} />
                                    <ChildProducts parentProduct={product} />
                                </>
                            ) : (
                                <ProductView product={product} fibblConfig={fibblConfig} />
                            )}

                            {/* RGBARRY-CUSTOMIZATION: Description accordion (shortDescription HTML), open by default */}
                            {product.shortDescription && (
                                <Accordion
                                    type="single"
                                    collapsible
                                    defaultValue="features"
                                    className="w-full border-t border-[#e8e4e0]">
                                    <AccordionItem value="features" className="border-b border-[#e8e4e0]">
                                        <AccordionTrigger className="text-sm font-semibold tracking-wide uppercase py-4 hover:no-underline">
                                            Description
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div
                                                className={cn(
                                                    'text-sm text-[#3a3a3a] pb-2 [&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-4 [&_li]:leading-relaxed [&_li]:break-inside-avoid',
                                                    // RGBARRY-CUSTOMIZATION: long bullet lists wrap into two
                                                    // columns on desktop so they don't dwarf the product images
                                                    countListItems(product.shortDescription) > 6 &&
                                                        'md:columns-2 md:gap-x-8'
                                                )}
                                                // oxlint-disable-next-line react/no-danger -- product shortDescription is authored HTML from Commerce Cloud
                                                dangerouslySetInnerHTML={{
                                                    __html: product.shortDescription,
                                                }}
                                            />
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            )}

                            {/* A+ content asset — streams after recos, renders nothing on miss/error */}
                            <Suspense fallback={null}>
                                <Await resolve={aPlusContentPromise} errorElement={null}>
                                    {(html) =>
                                        html ? (
                                            <div
                                                className="w-full aplus-content"
                                                // oxlint-disable-next-line react/no-danger -- A+ content asset is authored HTML from Commerce Cloud
                                                dangerouslySetInnerHTML={{ __html: html }}
                                            />
                                        ) : null
                                    }
                                </Await>
                            </Suspense>

                            {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                            <UITarget targetId="sfcc.pdp.reviews.section" />
                            {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                            <UITarget targetId="sfcc.pdp.reviews.qna" />
                        </div>
                        {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                    </WriteReviewFormProvider>
                </ProductReviewsProvider>
                {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                {/* @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT */}
            </ProductContentDataProvider>
            {/* @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT */}
        </ProductProvider>
    );
}

/**
 * Product detail shell that composes the page layout with granular Suspense boundaries.
 * Regions render independently (they manage their own async via Suspense/Await),
 * while the core product content renders synchronously from the resolved loader data.
 */
function ProductDetailView({ loaderData }: { loaderData: ProductPageData }) {
    const { t } = useTranslation('product');
    const content = (
        <div className="min-h-screen bg-background">
            <div className="section-container pb-4 lg:pb-8">
                {/* Promo Content Region - Promotional content above main product */}
                <Region className="mb-8" page={loaderData.page} regionId="promoContent" />

                {/* Category breadcrumbs — sourced from the product's primary_category
                    expansion, so they render synchronously with the product (in SSR HTML for
                    SEO). Absent a primary category, the row renders nothing. */}
                {loaderData.product.primaryCategory ? (
                    <CategoryHrefProvider value={loaderData.categoryUrls}>
                        <CategoryBreadcrumbs category={loaderData.product.primaryCategory} />
                    </CategoryHrefProvider>
                ) : null}

                {/* Main Product Content — product is resolved synchronously by the loader */}
                <ProductContent
                    product={loaderData.product}
                    url={loaderData.pageUrl}
                    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
                    reviewsSummary={loaderData.reviewsSummary}
                    reviewsList={loaderData.reviewsList}
                    writeReviewForm={loaderData.writeReviewForm}
                    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
                    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
                    returnsWarrantyPromise={loaderData.returnsWarranty}
                    pdpCollapsiblesPromise={loaderData.pdpCollapsibles}
                    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
                    aPlusContentPromise={loaderData.aPlusContent}
                    fibblConfig={loaderData.fibblConfig}
                />

                {/* Engagement Content Region - PD editorial content when present */}
                <Region className="mt-16" page={loaderData.page} regionId="engagementContent" />

                {/* RGBARRY-CUSTOMIZATION: Einstein recommendations — always-on, not gated behind PD errorElement */}
                <div className="mt-16 space-y-16">
                    <ProductRecommendations
                        recommenderName={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
                        recommenderTitle={t('recommendations.youMightAlsoLike')}
                        className="max-w-none px-0"
                    />
                    <ProductRecommendations
                        recommenderName={EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED}
                        recommenderTitle={t('recommendations.recentlyViewed')}
                        className="max-w-none px-0"
                    />
                </div>
            </div>
        </div>
    );

    let finalContent = content;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    // RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP
    // finalContent = <PickupProvider>{finalContent}</PickupProvider>;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    // @sfdc-extension-block-start SFDC_EXT_BNPL
    finalContent = (
        <BnplProvider messagePromise={loaderData.bnplMessage} learnMorePromise={loaderData.bnplLearnMore}>
            {finalContent}
        </BnplProvider>
    );
    // @sfdc-extension-block-end SFDC_EXT_BNPL
    // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
    finalContent = (
        <ShippingDeliveryProvider estimatedDeliveryPromise={loaderData.estimatedDelivery}>
            {finalContent}
        </ShippingDeliveryProvider>
    );
    // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY

    return finalContent;
}

/**
 * Product page component that displays a product with its details and category breadcrumbs.
 * The page key ensures React only remounts when navigating to a different product, not variants.
 * @returns JSX element representing the product page with Suspense boundary
 */
export default function ProductPage({ loaderData }: { loaderData: ProductPageData }) {
    // Use pageKey from loaderData to force remount only when productId changes
    // This prevents showing skeleton when switching variants (pid parameter)
    const pageKey = loaderData.pageKey;

    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;

    return (
        <Fragment key={pageKey}>
            <ProductDetailView loaderData={loaderData} />

            {/* Product JSON-LD Schema for SEO - render after page content so it appears at end of body flow.
                JSON-LD is non-critical: errorElement renders nothing so a schema-generation failure
                silently degrades to no <script> tag. */}
            <Suspense fallback={null}>
                <Await resolve={loaderData.productSchema} errorElement={null}>
                    {(productSchema) =>
                        productSchema ? <JsonLd data={productSchema} id="product-schema" nonce={nonce} /> : null
                    }
                </Await>
            </Suspense>
        </Fragment>
    );
}
